import React, { useMemo, useState } from 'react';
import { Input } from '@schibsted-svp/react-ui';

import { CenteredSpinner } from '../../CenteredSpinner/CenteredSpinner';
import { PresetItem } from '../PresetItem/PresetItem';
import { useDebounce } from '../../../hooks/useDebounce';
import { useSearchAssets } from '../../../hooks/useSearchAssets';
import { useAssetById } from '../../../hooks/useAssetById';
import { getProvider } from '../../../services/svpProvider';
import { FormField } from '../../Form/shared/FormField/FormField';
import { getFirstUnselectedResult } from '../helpers';

import type { Provider, SvpAsset } from '../../../types';
import type { ChangeEvent, FC, KeyboardEvent } from 'react';

import styles from './StoriesSearch.module.scss';

export const defaultStoryFilters: Partial<
  Record<Provider, Record<string, string>>
> = {
  vgtv: {
    'additional.metadata.aspectRatio': '0.56',
    tagsid: '57eff209-28df-4a1a-bf47-17c2fe4244c5',
  },
  ab: {
    'additional.metadata.aspectRatio': '0.56',
  },
};

type Props = {
  selectedStories: SvpAsset[];
  onAddClick: (item: SvpAsset) => void;
  onRemoveClick: (item: SvpAsset) => void;
  label?: string;
};

export const StoriesSearch: FC<Props> = ({
  selectedStories = [],
  onAddClick,
  onRemoveClick,
  label = 'Search by title or ID',
}) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const numericId = /^\d+$/.test(debouncedQuery)
    ? Number(debouncedQuery)
    : undefined;

  const searchParams = useMemo(() => {
    if (!debouncedQuery) return undefined;
    return {
      query: debouncedQuery,
      filter: { ...defaultStoryFilters[getProvider()] },
    };
  }, [debouncedQuery]);

  const { data: stories = [], isFetching } = useSearchAssets(searchParams, {
    enabled: Boolean(debouncedQuery),
  });
  const { data: storyById } = useAssetById(numericId);

  const results = useMemo(() => {
    if (!storyById || stories.some(({ id }) => id === storyById.id)) {
      return stories;
    }

    return [storyById, ...stories];
  }, [stories, storyById]);

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return;
    }

    const selectedIds = selectedStories.map(({ id }) => id);
    const nextResult = getFirstUnselectedResult(results, selectedIds);

    if (nextResult) {
      onAddClick(nextResult);
      setQuery('');
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.search}>
        <FormField label={label}>
          <Input
            data-testid="preset-search-input"
            value={query}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;
              setQuery(value);
            }}
            onKeyDown={onInputKeyDown}
          />
        </FormField>
        <small className={styles.hint}>Press Enter to add the top result</small>
        {isFetching && <CenteredSpinner />}
        <ul className={styles.assetsList} data-testid="preset-results">
          {results.map((story) => {
            const isSelected = selectedStories
              .map((presetItem) => presetItem.id)
              .includes(story.id);

            return (
              <li key={story.id}>
                <PresetItem
                  item={story}
                  dataType={'stories'}
                  isSelected={isSelected}
                  onClick={() =>
                    isSelected ? onRemoveClick(story) : onAddClick(story)
                  }
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
